import React, { useMemo, useState } from 'react';
import { Play, Pause, Download, Eye, RotateCw, Trash2, Server, Square } from 'lucide-react';
import Button from '../shared/Button';
import Spinner from '../shared/Spinner';
import ConfirmModal from '../shared/ConfirmModal';
import { writeWorkbook, downloadBlob } from '../../utils/xlsx';
import { runIssuedScript } from './ToolkitManualRun';

// The results grid every row-by-row Toolkit screen shares: counts, search, status filter, Process All / Pause,
// a per-row Process and step log, and an Excel export of the whole set (the desktop's exportScriptResults).
//
// Props:
//   batch        — useToolkitBatch()
//   columns      — [{ key, label, render?(row) }]; also the export columns
//   searchKeys   — row keys the search box matches
//   exportName   — file name stem for the export
//   canRun       — false disables processing (e.g. no credential yet)
//   onClear      — optional: clears the loaded rows (and any stored state)
//   rowActions   — optional: [{ label, title, show(row), opts }] extra per-row buttons that re-run the row with
//                  `opts` (e.g. "Post anyway" past a date mismatch: { bypassDateCheck: true })
//
// A batch with a `server` (useToolkitBatch) also gets "Run on server" and the job's panel: progress, Stop / Resume,
// and its results. While a job runs, this tab's own Process buttons are off.

// The last ones are screen-specific (AR Accounts from the database, Internal Notes) and only offered in the filter when
// a row has them.
const STATUS_LABEL = { pending: 'Pending', processing: 'Processing', success: 'Success', skipped: 'Skipped', failed: 'Failed',
    'resync-pending': 'Re-send pending', 'not-found': 'Not in Cloud', waiting: 'Waiting for v5' };
const STATUSES = Object.keys(STATUS_LABEL);
const OPTIONAL_STATUSES = new Set(['resync-pending', 'not-found', 'waiting']);

const JOB_STATUS = { STARTING: 'Starting', RUNNING: 'Running on the server', SUCCEEDED: 'Finished', FAILED: 'Stopped with an error', STOPPED: 'Stopped' };
const when = (v) => (v ? new Date(v).toLocaleString() : '');

// The screen's server job (useServerJob): where it is, Stop / Resume, and its results as Excel.
function ServerJobPanel({ job: s }) {
    const j = s.job;
    return (
        <div className="tkb-step tkb-job">
            {j && (
                <div className="tkb-job-head">
                    <Server size={15} />
                    <strong>{JOB_STATUS[j.status] || j.status}</strong>
                    <span className="tkb-msg" style={{ margin: 0 }}>
                        {j.succeeded} succeeded · {j.skipped} skipped · {j.failed} failed{j.other ? ` · ${j.other} other` : ''} · {j.pending} to go — of {j.total}
                    </span>
                    <div className="tkb-toolbar-spacer" />
                    {s.active && <Button variant="secondary" onClick={s.stop} disabled={s.busy}><Square className="icon" /> Stop</Button>}
                    {!s.active && (j.pending > 0 || j.failed > 0) && (
                        <Button variant="secondary" onClick={s.resume} disabled={s.busy}><RotateCw className="icon" /> Resume on server</Button>
                    )}
                    <Button variant="secondary" onClick={s.download}><Download className="icon" /> Job results</Button>
                </div>
            )}
            {j && (
                <p className="tkb-hint" style={{ margin: '0.3rem 0 0' }}>
                    Job {j.id}{j.runNo > 1 ? ` (run ${j.runNo})` : ''} — started {when(j.startedAt)}{j.startedBy ? ` by ${j.startedBy}` : ''}
                    {j.finishedAt && !s.active ? `, ended ${when(j.finishedAt)}` : ''}.
                    {s.active && ' Rows update here as they finish; this page can be closed.'}
                    {!s.matches && ' It ran on a different set of rows from the grid above, so its results are not shown there — download them.'}
                </p>
            )}
            {!!s.waiting.length && (
                <div className="tkb-write-confirm" style={{ marginTop: '0.5rem' }}>
                    <div className="tkb-write-head">The job is waiting for {s.waiting.length} script(s) to be run by hand (no SyncConnect)</div>
                    <ul className="tkb-warn-list">
                        {s.waiting.map((w) => (
                            <li key={w.i}>
                                {w.key || w.title} — request {w.queryId}{' '}
                                <button type="button" className="tkb-link" onClick={() => runIssuedScript(w.queryId).catch(() => {})}>Run it yourself</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {j?.error && <p className="tkb-msg tkb-fail">{j.error}</p>}
            {s.message && <p className="tkb-msg">{s.message}</p>}
        </div>
    );
}

function ToolkitBatchTable({ batch, columns, searchKeys, exportName, canRun = true, onClear, rowActions = [] }) {
    const { rows, runOne, runAll, pause, current, counts, remaining, job } = batch;
    // While a server job runs these rows, this tab does not run them too.
    const running = batch.running || !!job?.active;
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [log, setLog] = useState(null);
    const [confirmClear, setConfirmClear] = useState(false);

    const shown = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => status === 'all' || row.status === status)
            .filter(({ row }) => !term || searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(term)));
    }, [rows, search, status, searchKeys]);

    const exportXlsx = async () => {
        const cols = [...columns.map((c) => c.label), 'Status', 'Message'];
        const data = rows.map((r) => [...columns.map((c) => (r[c.key] ?? '')), STATUS_LABEL[r.status] || r.status, r.errorMessage || '']);
        downloadBlob(await writeWorkbook([{ name: exportName, columns: cols, rows: data }]),
            `${exportName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    if (!rows.length) return null;

    return (
        <>
            <div className="otx-stats">
                <div className="otx-stat"><span className="otx-stat-label">Rows</span><span className="otx-stat-value">{rows.length}</span></div>
                <div className="otx-stat"><span className="otx-stat-label">Success</span><span className="otx-stat-value otx-ok">{counts.success || 0}</span></div>
                <div className="otx-stat"><span className="otx-stat-label">Skipped</span><span className="otx-stat-value">{counts.skipped || 0}</span></div>
                <div className="otx-stat"><span className="otx-stat-label">Failed</span><span className="otx-stat-value otx-fail">{counts.failed || 0}</span></div>
                <div className="otx-stat"><span className="otx-stat-label">Remaining</span><span className="otx-stat-value">{remaining}</span></div>
            </div>

            <div className="tkb-toolbar">
                <input className="tkb-search" type="search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className="tkb-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    {STATUSES.filter((s) => !OPTIONAL_STATUSES.has(s) || counts[s]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}{counts[s] ? ` (${counts[s]})` : ''}</option>)}
                </select>
                <div className="tkb-toolbar-spacer" />
                {batch.running
                    ? <Button variant="secondary" onClick={pause}><Pause className="icon" /> Pause</Button>
                    : <Button variant="success" onClick={runAll} disabled={!canRun || !remaining || !!job?.active}>
                        <Play className="icon" /> {counts.success || counts.failed || counts.skipped ? 'Resume' : 'Process all'}
                    </Button>}
                {job?.enabled && (
                    <Button variant="secondary" onClick={job.runOnServer} disabled={!canRun || !remaining || running || job.busy}
                        title="Run the remaining rows on the server — it carries on if you close this page, and emails you when it is done">
                        <Server className="icon" /> Run on server
                    </Button>
                )}
                <Button variant="secondary" onClick={exportXlsx}><Download className="icon" /> Export</Button>
                {onClear && <Button variant="secondary" onClick={() => setConfirmClear(true)} disabled={running}><Trash2 className="icon" /> Clear</Button>}
            </div>

            {job?.enabled && (job.job || job.message) && <ServerJobPanel job={job} />}

            <div className="otx-table-wrap">
                <table className="otx-table">
                    <thead>
                        <tr>
                            {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                            <th>Status</th>
                            <th>Message</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map(({ row, index }) => (
                            <tr key={index} className={index === current ? 'tkb-row-current' : undefined}>
                                {columns.map((c) => <td key={c.key}>{c.render ? c.render(row) : (row[c.key] ?? '')}</td>)}
                                <td>
                                    {row.status === 'processing'
                                        ? <Spinner />
                                        : <span className={`tkb-badge tkb-status-${row.status}`}>{STATUS_LABEL[row.status] || row.status}</span>}
                                </td>
                                <td className="tkb-message">{row.errorMessage || ''}</td>
                                <td className="tkb-actions">
                                    {!!(row.steps && row.steps.length) && (
                                        <button type="button" className="tkb-icon-btn" title="Step log" onClick={() => setLog(row)}><Eye size={14} /></button>
                                    )}
                                    {!running && canRun && row.status !== 'success' && (
                                        <button type="button" className="tkb-icon-btn" title="Process this row" onClick={() => runOne(index)}><RotateCw size={14} /></button>
                                    )}
                                    {!running && canRun && rowActions.filter((a) => a.show(row)).map((a) => (
                                        <button key={a.label} type="button" className="tkb-text-btn" title={a.title} onClick={() => runOne(index, a.opts)}>{a.label}</button>
                                    ))}
                                </td>
                            </tr>
                        ))}
                        {!shown.length && <tr><td colSpan={columns.length + 3} className="otx-empty">No rows match.</td></tr>}
                    </tbody>
                </table>
            </div>

            {log && (
                <ConfirmModal title={`Steps — ${log[columns[0].key] ?? ''}`} cancelLabel="Close" hideConfirm wide onCancel={() => setLog(null)}>
                    <ol className="tkb-steps-log">
                        {log.steps.map((s, i) => (
                            <li key={i} className={`tkb-steplog-${s.status}`}>
                                <strong>{s.step}</strong> — {s.status}
                                {s.error && <div className="tkb-fail">{s.error}</div>}
                                {s.result && <pre className="tkb-pre">{JSON.stringify(s.result, null, 2)}</pre>}
                            </li>
                        ))}
                    </ol>
                </ConfirmModal>
            )}
            {confirmClear && (
                <ConfirmModal title="Clear all rows?" message="The loaded rows and their results are removed. Nothing in OPERA Cloud is changed."
                    confirmLabel="Clear" danger onCancel={() => setConfirmClear(false)}
                    onConfirm={() => { setConfirmClear(false); onClear(); }} />
            )}
        </>
    );
}

export default ToolkitBatchTable;
